import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  ArrowLeft, Mail, Clock, Calendar, Trash2, Send, 
  AlertCircle, CheckCircle, Building2, User, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from 'sonner';
import HelpIcon from '@/components/HelpIcon';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ScheduledEmailsPage() {
  const navigate = useNavigate();
  const [emails, setEmails] = useState([]);
  const [byDate, setByDate] = useState({});
  const [totalPending, setTotalPending] = useState(0);
  const [loading, setLoading] = useState(true);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [emailToCancel, setEmailToCancel] = useState(null);
  
  const darkMode = document.documentElement.classList.contains('dark');

  useEffect(() => {
    fetchScheduledEmails();
  }, []);

  const fetchScheduledEmails = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/scheduled-emails`);
      setEmails(response.data.emails || []);
      setByDate(response.data.by_date || {});
      setTotalPending(response.data.total_pending || 0);
    } catch (error) {
      console.error('Error fetching scheduled emails:', error);
      toast.error('Failed to load scheduled emails');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelEmail = async () => {
    if (!emailToCancel) return;
    
    try {
      await axios.delete(`${API}/scheduled-emails/${emailToCancel.id}`);
      toast.success('Scheduled email cancelled');
      setCancelDialogOpen(false);
      setEmailToCancel(null);
      fetchScheduledEmails();
    } catch (error) {
      console.error('Error cancelling email:', error);
      toast.error('Failed to cancel email');
    }
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return 'Not scheduled';
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  const getTimeUntil = (dateStr) => {
    if (!dateStr) return '';
    const now = new Date();
    const scheduled = new Date(dateStr);
    const diffMs = scheduled - now;
    
    if (diffMs < 0) return 'Overdue';
    
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);
    
    if (diffDays > 0) return `${diffDays}d ${diffHours % 24}h`;
    if (diffHours > 0) return `${diffHours}h`;
    return 'Soon';
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-[#0D0D0D]' : 'bg-[#FAF9F7]'}`}>
      {/* Header */}
      <div className={`border-b ${darkMode ? 'border-[#2A2A2A] bg-[#0D0D0D]' : 'border-[#E5E2DD] bg-white'}`}>
        <div className="max-w-6xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button 
                variant="ghost" 
                size="icon" 
                onClick={() => navigate('/')}
                className={darkMode ? 'text-[#E5E2DD] hover:bg-[#1A1A1A]' : ''}
              >
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <div className="flex items-center gap-2">
                  <Mail className="w-5 h-5 text-[#BC5D41]" />
                  <h1 className={`text-xl font-heading ${darkMode ? 'text-white' : 'text-[#1A1918]'}`}>
                    Scheduled Emails
                  </h1>
                  <HelpIcon elementId="scheduled-emails-dashboard" position="inline" />
                </div>
                <p className={`text-sm ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
                  View and manage emails scheduled to be sent
                </p>
              </div>
            </div>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchScheduledEmails}
              disabled={loading}
              className={darkMode ? 'border-[#3A3A3A] text-[#E5E2DD]' : ''}
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="max-w-6xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                <Clock className="w-4 h-4" />
                Pending Emails
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-3xl font-bold ${darkMode ? 'text-[#BC5D41]' : 'text-[#70170F]'}`}>
                {totalPending}
              </p>
            </CardContent>
          </Card>
          
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                <Calendar className="w-4 h-4" />
                Scheduled Days
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-3xl font-bold ${darkMode ? 'text-blue-400' : 'text-blue-600'}`}>
                {Object.keys(byDate).length}
              </p>
            </CardContent>
          </Card>
          
          <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
            <CardHeader className="pb-2">
              <CardTitle className={`text-sm font-medium flex items-center gap-2 ${darkMode ? 'text-gray-400' : 'text-muted-foreground'}`}>
                <Send className="w-4 h-4" />
                Next Send
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className={`text-lg font-medium ${darkMode ? 'text-[#E5E2DD]' : 'text-[#1A1918]'}`}>
                {emails.length > 0 ? formatDateTime(emails[0].scheduled_for) : 'None scheduled'}
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Emails Table */}
        <Card className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <CardHeader>
            <CardTitle className={`flex items-center gap-2 ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
              <Mail className="w-5 h-5 text-[#BC5D41]" />
              All Scheduled Emails
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="w-6 h-6 animate-spin text-[#BC5D41]" />
              </div>
            ) : emails.length === 0 ? (
              <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
                <Mail className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p className="text-lg font-medium">No scheduled emails</p>
                <p className="text-sm">Emails scheduled from the project wizard will appear here</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow className={darkMode ? 'border-[#2A2A2A]' : ''}>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Scheduled For</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>To</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Subject</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Project</TableHead>
                    <TableHead className={darkMode ? 'text-gray-400' : ''}>Status</TableHead>
                    <TableHead className={`text-right ${darkMode ? 'text-gray-400' : ''}`}>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {emails.map((email) => (
                    <TableRow key={email.id} className={darkMode ? 'border-[#2A2A2A]' : ''}>
                      <TableCell>
                        <div>
                          <p className={`font-medium ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                            {formatDateTime(email.scheduled_for)}
                          </p>
                          <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
                            {getTimeUntil(email.scheduled_for)}
                          </p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-[#BC5D41]" />
                          <span className={darkMode ? 'text-[#E5E2DD]' : ''}>{email.to_email}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className={`max-w-[200px] truncate block ${darkMode ? 'text-[#E5E2DD]' : ''}`}>
                          {email.subject}
                        </span>
                      </TableCell>
                      <TableCell>
                        {email.project_name ? (
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-blue-500" />
                            <div>
                              <p className={`text-sm ${darkMode ? 'text-[#E5E2DD]' : ''}`}>{email.project_name}</p>
                              {email.client_name && (
                                <p className={`text-xs ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>
                                  {email.client_name}
                                </p>
                              )}
                            </div>
                          </div>
                        ) : (
                          <span className={`text-sm ${darkMode ? 'text-gray-500' : 'text-muted-foreground'}`}>-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Badge className="bg-amber-100 text-amber-700">
                          <Clock className="w-3 h-3 mr-1" />
                          Scheduled
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => {
                            setEmailToCancel(email);
                            setCancelDialogOpen(true);
                          }}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Cancel Confirmation Dialog */}
      <AlertDialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <AlertDialogContent className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
          <AlertDialogHeader>
            <AlertDialogTitle className={darkMode ? 'text-[#E5E2DD]' : ''}>
              Cancel Scheduled Email?
            </AlertDialogTitle>
            <AlertDialogDescription className={darkMode ? 'text-gray-400' : ''}>
              This will permanently cancel the scheduled email to{' '}
              <strong>{emailToCancel?.to_email}</strong>. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className={darkMode ? 'border-[#3A3A3A] text-[#E5E2DD]' : ''}>
              Keep Scheduled
            </AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleCancelEmail}
              className="bg-red-600 hover:bg-red-700"
            >
              Cancel Email
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
